import { flexRender } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import { Fragment, Key, useCallback, useEffect, useRef, useState } from 'react';
import { PiCaretDownFill, PiCaretUpFill } from 'react-icons/pi';
import { Empty, Text, Title } from 'rizzui';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import CustomSpinner from '../../../loader/CustomSpinner';
import { CustomBodyCellProps, CustomHeaderCellProps, MainTableProps, PinnedRowProps, } from '../../../type/main-table-types';
import cn from '../../../utils/classNames';
import { getColumnOptions } from '../util';
import DragDropWrapper from './DragDropWrapper';
import RowWithDropdown from './RowWithDropdown';
import SortableTableRow from './SortableTableRow';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table';

export interface RowColorRule {
    column: string;
    values: string[] | null;
    bgColor?: string; // Ahora opcional para compatibilidad con gradientes
    rowBorder?: string;
    gradientBg?: string; // Nueva propiedad para degradados de fondo
    useGradient?: boolean; // Flag para activar el uso de degradados
}

export type RowColorRules = RowColorRule[];


export default function MainTable<TData extends Record<string, any>>({
    table,
    variant,
    largeTable,
    classNames,
    columnOrder,
    isLoading = false,
    floatingHorizontalScrollbar = false,
    showLoadingText = false,
    components,
    updateFilterOrder,
    defaultOrder,
    isSortable = true,
    columnsNoSortables = [],
    classNameTable = '',
    customRowsBackgroundColors = [],
    divClassName = '',
    headerTableClassName = '',
    footerRow = null,
    renderRowDropdown,
    onDragEnd
}: MainTableProps<TData> & { footerRow?: (() => Record<string, any>) | null, renderRowDropdown?: ((row: any) => Record<string, any> | null) | null }) {

    const { containerRef, tableRef, isLeftScrollable, isRightScrollable } = useScrollPosition();
    const [openDropdownRow, setOpenDropdownRow] = useState(true);
    const floatingScrollbarRef = useRef<HTMLDivElement | null>(null);
    const [floatingScrollbarMetrics, setFloatingScrollbarMetrics] = useState<{
        left: number;
        width: number;
        isInView: boolean;
        isOverflowingX: boolean;
        contentWidth: number;
    }>({
        left: 0,
        width: 0,
        isInView: false,
        isOverflowingX: false,
        contentWidth: 0,
    });

    const headerParam = {
        table,
        columnOrder,
        isLeftScrollable,
        isRightScrollable,
    };

    const rowParam = {
        table,
        columnOrder,
        isLeftScrollable,
        isRightScrollable,
    };

    const mainRows = table?.getIsSomeRowsPinned() ? table.getCenterRows() : table?.getRowModel().rows;

    // Create items array for drag and drop context
    const dragDropItems = mainRows?.map(row => row.id) || [];
    const isDragDropEnabled = !!onDragEnd;

    const updateFloatingScrollbarMetrics = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const isInView = rect.bottom > window.innerHeight && rect.top < window.innerHeight;
        const contentWidth = container.scrollWidth;
        const isOverflowingX = contentWidth > container.clientWidth + 1;
        setFloatingScrollbarMetrics((prev) => {
            const next = { left: rect.left, width: rect.width, isInView, isOverflowingX, contentWidth };
            if (
                prev.left === next.left &&
                prev.width === next.width &&
                prev.isInView === next.isInView &&
                prev.isOverflowingX === next.isOverflowingX &&
                prev.contentWidth === next.contentWidth
            ) return prev;
            return next;
        });
    }, [containerRef]);

    const showFloatingScrollbar =
        floatingHorizontalScrollbar && floatingScrollbarMetrics.isInView && floatingScrollbarMetrics.isOverflowingX;

    useEffect(() => {
        if (!floatingHorizontalScrollbar) return;
        updateFloatingScrollbarMetrics();
        window.addEventListener('scroll', updateFloatingScrollbarMetrics, { passive: true });
        window.addEventListener('resize', updateFloatingScrollbarMetrics);
        return () => {
            window.removeEventListener('scroll', updateFloatingScrollbarMetrics);
            window.removeEventListener('resize', updateFloatingScrollbarMetrics);
        };
    }, [floatingHorizontalScrollbar, updateFloatingScrollbarMetrics]);

    useEffect(() => {
        if (!showFloatingScrollbar) return;
        const container = containerRef.current;
        const floating = floatingScrollbarRef.current;
        if (!container || !floating) return;

        let syncingFromContainer = false;
        let syncingFromFloating = false;

        const onContainerScroll = () => {
            if (syncingFromFloating) {
                syncingFromFloating = false;
                return;
            }
            syncingFromContainer = true;
            floating.scrollLeft = container.scrollLeft;
        };

        const onFloatingScroll = () => {
            if (syncingFromContainer) {
                syncingFromContainer = false;
                return;
            }
            syncingFromFloating = true;
            container.scrollLeft = floating.scrollLeft;
        };

        container.addEventListener('scroll', onContainerScroll, { passive: true });
        floating.addEventListener('scroll', onFloatingScroll, { passive: true });
        floating.scrollLeft = container.scrollLeft;

        return () => {
            container.removeEventListener('scroll', onContainerScroll);
            floating.removeEventListener('scroll', onFloatingScroll);
        };
    }, [containerRef, showFloatingScrollbar]);

    if (!table) return null;

    if (isLoading) {
        return (
            <div className={`flex h-full min-h-[128px] flex-col items-center justify-center ${divClassName}`}>
                <CustomSpinner />
                {showLoadingText ? <Title as="h6" className="-me-2 mt-4 font-medium text-gray-500">Cargando...</Title> : null}
            </div>
        );
    };

    return (
        <>
            {showFloatingScrollbar && (
                <div
                    className="fixed bottom-0 z-[980] bg-gray-0/80 backdrop-blur-xl"
                    style={{ left: floatingScrollbarMetrics.left, width: floatingScrollbarMetrics.width }}
                >
                    <div
                        ref={floatingScrollbarRef}
                        className="scrollbar w-full overflow-x-auto overflow-y-hidden"
                        style={{ height: 15 }}
                    >
                        <div style={{ width: Math.max(floatingScrollbarMetrics.contentWidth, floatingScrollbarMetrics.width + 1), height: 1 }} />
                    </div>
                </div>
            )}
            <div
                ref={containerRef}
                className={cn('custom-scrollbar w-full max-w-full overflow-x-auto', divClassName, classNames?.container)}
            >
                <Table
                    ref={tableRef}
                    variant={variant}
                    style={{ width: table?.getTotalSize() }}
                    className={cn(largeTable && 'table-large', classNameTable)}
                >
                    <Fragment>
                        {components?.header
                            ? components.header(headerParam)
                            : (
                                <TableHeader>
                                    {table?.getHeaderGroups().map((headerGroup) => {
                                        const headerCellParam = {
                                            columnOrder,
                                            headerGroup,
                                            isLeftScrollable,
                                            isRightScrollable,
                                            updateFilterOrder,
                                            defaultOrder,
                                            isSortable,
                                        };
                                        return (
                                            <TableRow key={headerGroup.id}>
                                                {components?.headerCell
                                                    ? components.headerCell(headerCellParam)
                                                    : (
                                                        <TableHeadBasic
                                                            headerGroup={headerGroup}
                                                            isLeftScrollable={isLeftScrollable}
                                                            isRightScrollable={isRightScrollable}
                                                            updateFilterOrder={updateFilterOrder}
                                                            defaultOrder={defaultOrder}
                                                            largeTable={largeTable}
                                                            isSortable={isSortable}
                                                            columnNoSortables={columnsNoSortables}
                                                            headerTableClassName={headerTableClassName}
                                                        />
                                                    )}
                                            </TableRow>
                                        );
                                    })}
                                </TableHeader>
                            )}
                    </Fragment>

                    <DragDropWrapper onDragEnd={onDragEnd} items={dragDropItems} mainRows={mainRows}>
                        <TableBody>
                            {table?.getTopRows().map((row) => (
                                <PinnedRow
                                    key={row.id}
                                    row={row}
                                    table={table}
                                    isLeftScrollable={isLeftScrollable}
                                    isRightScrollable={isRightScrollable}
                                />
                            ))}

                            {components?.bodyRow
                                ? components.bodyRow(rowParam)
                                : (
                                    <>
                                        {mainRows?.map((row) => {
                                            let rowBgColor = '';
                                            let rowBorder = '';
                                            if (customRowsBackgroundColors && customRowsBackgroundColors.length > 0) {
                                                for (const rule of customRowsBackgroundColors) {
                                                    const cellValue = row.original?.[rule.column];
                                                    if ((cellValue == null && rule.values == null) || rule?.values?.includes(cellValue)) {
                                                        // Priorizar gradientBg si está disponible
                                                        if (rule.gradientBg) {
                                                            rowBgColor = rule.gradientBg;
                                                        } else if (rule.bgColor) {
                                                            // Usar color sólido como fallback
                                                            rowBgColor = rule.bgColor;
                                                        }
                                                        rowBorder = rule.rowBorder ? rule.rowBorder : '';
                                                        break;
                                                    }
                                                }
                                            }

                                            if (renderRowDropdown !== null) {
                                                return (
                                                    <RowWithDropdown
                                                        key={row.id}
                                                        row={row}
                                                        rowBgColor={rowBgColor}
                                                        rowBorder={rowBorder}
                                                        isLeftScrollable={isLeftScrollable}
                                                        isRightScrollable={isRightScrollable}
                                                        components={components}
                                                        renderRowDropdown={renderRowDropdown}
                                                    />
                                                );
                                            }
                                            return (
                                                <Fragment key={row.id}>
                                                    <SortableTableRow
                                                        id={row.id}
                                                        className={rowBgColor + ' ' + rowBorder}
                                                        isDragEnabled={isDragDropEnabled}
                                                    >
                                                        {/* Render cells */}
                                                        {row.getVisibleCells().map((cell) => {
                                                            const bodyCellParam = {
                                                                cell,
                                                                columnOrder,
                                                                isLeftScrollable,
                                                                isRightScrollable,
                                                            };
                                                            return (
                                                                <Fragment key={cell.id}>
                                                                    {components?.bodyCell
                                                                        ? components.bodyCell(bodyCellParam)
                                                                        : (
                                                                            <TableCellBasic
                                                                                cell={cell}
                                                                                isLeftScrollable={isLeftScrollable}
                                                                                isRightScrollable={isRightScrollable}
                                                                            />
                                                                        )}
                                                                </Fragment>
                                                            );
                                                        })}
                                                    </SortableTableRow>
                                                    {/* custom-expanded-component start  */}
                                                    {components?.expandedComponent && row.getIsExpanded() && (
                                                        <TableRow>
                                                            <TableCell className="!p-0" colSpan={row.getVisibleCells().length}>
                                                                {components.expandedComponent(row)}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {/* customExpandedComponent end */}
                                                </Fragment>
                                            );
                                        })}
                                    </>
                                )}

                            {table?.getBottomRows().map((row) => (
                                <PinnedRow
                                    key={row.id}
                                    row={row}
                                    table={table}
                                    isLeftScrollable={isLeftScrollable}
                                    isRightScrollable={isRightScrollable}
                                />
                            ))}
                            {footerRow && (
                                <TableRow className="bg-gray-50 font-semibold">
                                    {table.getVisibleLeafColumns().map((col) => (
                                        <TableCell key={col.id}>
                                            {col.columnDef.footer ? '' : footerRow()[col.id] || ''}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                        </TableBody>
                    </DragDropWrapper>
                </Table>
            </div>

            {isEmpty(table?.getRowModel().rows) && (
                <div className="py-5 text-center lg:py-8">
                    <Empty /> <Text className="mt-3">No hay resultados</Text>
                </div>
            )}
        </>
    );
}

// Basic Header component
export function TableHeadBasic<TData extends Record<string, any>>({
    headerGroup,
    isLeftScrollable,
    isRightScrollable,
    updateFilterOrder,
    defaultOrder,
    isSortable,
    columnNoSortables,
    headerTableClassName
}: CustomHeaderCellProps<TData>) {
    const [sortedBy, setSortedBy] = useState<{ id: Key; direction: 'asc' | 'desc' | null } | null>(
        defaultOrder !== undefined ? { id: defaultOrder[0]?.field, direction: defaultOrder[0]?.order } : null
    );

    const handleSort = (columnId: Key) => {
        if (sortedBy?.id === columnId) {
            const newDirection = sortedBy.direction === 'asc' ? 'desc' : 'asc';
            setSortedBy({ id: columnId, direction: newDirection });
            updateFilterOrder && updateFilterOrder(columnId, newDirection);
        } else {
            setSortedBy({ id: columnId, direction: 'asc' });
            updateFilterOrder && updateFilterOrder(columnId, 'asc');
        }
    };

    if (!headerGroup) return null;

    return (
        <>
            {headerGroup.headers.map((header) => {
                const { canResize, canPin, isPinned, isLeftPinned, isRightPinned } = getColumnOptions(header.column);

                return (
                    <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                            left: isLeftPinned ? header.column.getStart('left') : undefined,
                            right: isRightPinned ? header.column.getAfter('right') : undefined,
                            width: header.getSize(),
                        }}
                        className={cn(
                            'group text-nowrap row whitespace-nowrap',
                            isPinned && canPin && 'sticky z-10',
                            !isPinned && canResize && 'relative',
                            isPinned && isLeftScrollable && 'sticky-right',
                            isPinned && isRightScrollable && 'sticky-left',
                            // @ts-ignore
                            header.column.columnDef.meta?.colHeaderClassName || ''
                        )}
                    >
                        {header.isPlaceholder
                            ? null
                            : (<div className={cn(sortedBy?.id === header.column.id && 'text-primary', headerTableClassName)}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {(isSortable && columnNoSortables && columnNoSortables.indexOf(header.column.id) === -1 && header.column.getCanSort())
                                    ? (
                                        <button
                                            type="button"
                                            onClick={() => handleSort(header.column.id)}
                                            className="ms-2 translate-y-1"
                                            aria-label="Ordenar por esta columna"
                                        >
                                            {{
                                                asc: <PiCaretUpFill size={14} color={sortedBy?.id === header.column.id ? 'black' : 'gray'} />,
                                                desc: <PiCaretDownFill size={14} color={sortedBy?.id === header.column.id ? 'black' : 'gray'} />,
                                            }[(sortedBy?.id === header.column.id ? sortedBy.direction : 'asc') as keyof typeof sortedBy] ??
                                                (header.column.columnDef.header !== '' && (
                                                    <PiCaretDownFill size={14} color={sortedBy?.id === header.column.id ? 'black' : 'gray'} />
                                                ))}
                                        </button>
                                    )
                                    : null
                                }
                            </div>)
                        }


                        {header.column.getCanResize() && (
                            <div
                                {...{
                                    onDoubleClick: () => header.column.resetSize(),
                                    onMouseDown: header.getResizeHandler(),
                                    onTouchStart: header.getResizeHandler(),
                                }}
                                className="absolute end-0 top-0 hidden h-full w-0.5 cursor-w-resize bg-gray-400 group-hover:block"
                            />
                        )}
                    </TableHead>
                );
            })}
        </>
    );
}

// Basic Cell component
export function TableCellBasic<TData extends Record<string, any>>({
    cell,
    isLeftScrollable,
    isRightScrollable,
}: CustomBodyCellProps<TData>) {
    if (!cell) return null;

    const { canResize, canPin, isPinned, isLeftPinned, isRightPinned } =
        getColumnOptions(cell.column);

    return (
        <TableCell
            style={{
                left: isLeftPinned ? cell!.column.getStart('left') : undefined,
                right: isRightPinned ? cell.column.getAfter('right') : undefined,
                width: cell.column.getSize(),
            }}
            className={cn(
                isPinned && canPin && 'sticky z-10',
                !isPinned && canResize && 'relative',
                isPinned && isLeftScrollable && 'sticky-right',
                isPinned && isRightScrollable && 'sticky-left'
            )}
        >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
    );
}

// Pinned Row Component
export function PinnedRow<TData extends Record<string, any>>({
    row,
    isLeftScrollable,
    isRightScrollable,
}: PinnedRowProps<TData>) {
    const isTopPinned = row.getIsPinned() === 'top';
    const isBottomPinned = row.getIsPinned() === 'bottom';
    return (
        <TableRow
            className={cn(
                'sticky z-20 bg-white dark:bg-gray-50',
                isTopPinned && '-top-px shadow-[0px_2px_2px_0px_#0000000D]',
                isBottomPinned && '-bottom-0.5 shadow-[rgba(0,0,0,0.24)_0px_3px_8px]'
            )}
        >
            {row.getVisibleCells().map((cell) => {
                return (
                    <TableCellBasic
                        key={cell.id}
                        cell={cell}
                        isLeftScrollable={isLeftScrollable}
                        isRightScrollable={isRightScrollable}
                    />
                );
            })}
        </TableRow>
    );
}
